import type { SEOHandle } from "@nasa-gcn/remix-seo";
import type { LoaderFunctionArgs, MetaFunction } from "@remix-run/node";
import { Link, useLoaderData } from "@remix-run/react";

import { CourseCard } from "~/components/courses/CourseCard";
import { SUPPORTED_LANGUAGES } from "~/config/constants";
import { useBooking } from "~/context/BookingContext";
import {
  COURSES,
  COURSE_SLUGS,
  CourseSlug,
  getCourse,
  getRelatedCourses,
} from "~/data/courses";
import { getOfferFor } from "~/data/pricing";
import { HIGH_FETCH_PRIORITY } from "~/helpers/imagePriority";
import { useLocalizedPath } from "~/helpers/navigationHelper";
import common from "~/i18n/common";
import coursesDict from "~/i18n/courses";
import pages from "~/i18n/pages";
import { useT } from "~/i18n/useT";
import { buildMeta, langFromParams } from "~/seo/meta";
import { breadcrumbSchema, courseSchema } from "~/seo/schema";

import "./course.scss";

function isCourseSlug(value: string | undefined): value is CourseSlug {
  return !!value && (COURSE_SLUGS as readonly string[]).includes(value);
}

export async function loader({ params, request }: LoaderFunctionArgs) {
  if (!isCourseSlug(params.slug)) {
    throw new Response("Not Found", { status: 404 });
  }

  const country = (
    request.headers.get("X-Vercel-IP-Country") || "US"
  ).toLowerCase();

  return { slug: params.slug, country };
}

export const meta: MetaFunction<typeof loader> = ({ params, data }) => {
  const lang = langFromParams(params);
  const slug = data?.slug;
  if (!slug) return [];

  const copy = coursesDict[lang][slug];
  const offer = getOfferFor(data.country, "groupCourses", "semiIntensive");

  return [
    ...buildMeta({
      lang,
      path: `/course/${slug}`,
      title: `SkillSynergy | ${copy.title}`,
      description: copy.short,
    }),
    {
      "script:ld+json": courseSchema({
        lang,
        slug,
        title: copy.title,
        description: copy.long,
        price: offer.price,
        currency: offer.currency,
      }),
    },
    {
      "script:ld+json": breadcrumbSchema(lang, [
        { name: common[lang].a11y.home, path: "/" },
        { name: common[lang].nav.courses, path: "/courses" },
        { name: copy.title, path: `/course/${slug}` },
      ]),
    },
  ];
};

// One sitemap entry per course, per locale.
export const handle: SEOHandle = {
  getSitemapEntries: async () =>
    COURSES.flatMap((course) =>
      SUPPORTED_LANGUAGES.map((locale) => ({
        route:
          locale === "en"
            ? `/course/${course.slug}`
            : `/${locale}/course/${course.slug}`,
        priority: 0.8 as const,
        changefreq: "weekly" as const,
      })),
    ),
};

export default function CourseDetailPage() {
  const { slug } = useLoaderData<typeof loader>();
  const cd = useT(coursesDict);
  const p = useT(pages).coursePage;
  const c = useT(common);
  const localize = useLocalizedPath();
  const booking = useBooking();

  const course = getCourse(slug);
  const copy = cd[slug];
  const related = getRelatedCourses(slug);

  return (
    <main id="main-content">
      <section className="ss-container ss-course-hero">
        <nav className="ss-crumbs" aria-label={c.a11y.breadcrumb}>
          <Link to={localize("/")}>{c.a11y.home}</Link>
          <span aria-hidden="true"> / </span>
          <Link to={localize("/courses")}>{c.nav.courses}</Link>
          <span aria-hidden="true"> / </span>
          <span aria-current="page">{copy.title}</span>
        </nav>

        <div className="ss-course-hero__grid">
          <div>
            <div className="ss-course-hero__tags">
              <span className="ss-tag ss-tag--blue">{copy.level}</span>
              <span className="ss-tag ss-tag--neutral">{copy.duration}</span>
              {course.isNew ? (
                <span className="ss-badge">{cd.badgeNew}</span>
              ) : null}
            </div>

            <h1 className="ss-course-hero__title">{copy.title}</h1>
            <p className="ss-course-hero__lead">{copy.long}</p>

            <div className="ss-course-hero__actions">
              <Link
                to={localize("/contact")}
                className="ss-btn ss-btn--primary"
              >
                {c.cta.enrollNow}
              </Link>
              <Link
                to={localize("/pricing")}
                className="ss-btn ss-btn--outline"
              >
                {c.cta.seePricing}
              </Link>
            </div>
          </div>

          <div className="ss-thumb ss-course-hero__media">
            <img
              src={course.image}
              alt=""
              width={560}
              height={420}
              loading="eager"
              {...HIGH_FETCH_PRIORITY}
              decoding="async"
            />
          </div>
        </div>
      </section>

      <section className="ss-section ss-container">
        <p className="ss-eyebrow">{p.curriculumEyebrow}</p>
        <h2 className="ss-h2 ss-course__curriculum-title">
          {p.curriculumTitle}
        </h2>

        <ol className="ss-grid-3 ss-topics">
          {copy.topics.map((topic, index) => (
            <li key={topic} className="ss-card ss-topics__item">
              <span className="ss-topics__n" aria-hidden="true">
                {index + 1}
              </span>
              <span className="ss-topics__label">{topic}</span>
            </li>
          ))}
        </ol>
      </section>

      <section className="ss-section ss-container">
        <div className="ss-course__related-head">
          <h2 className="ss-h2 ss-course__related-title">{p.exploreOthers}</h2>
          <Link to={localize("/courses")} className="ss-link-mono">
            {c.cta.viewAll}
            <span className="ss-flip" aria-hidden="true">
              →
            </span>
          </Link>
        </div>

        <div className="ss-grid-3">
          {related.map((item) => (
            <CourseCard key={item.slug} course={item} />
          ))}
        </div>
      </section>

      <section className="ss-section ss-container">
        <div className="ss-panel ss-panel--dark ss-cta-dark">
          <div>
            <h2 className="ss-cta-dark__title">{p.readyTitle(copy.title)}</h2>
            <p className="ss-cta-dark__body">{p.readyBody}</p>
          </div>
          <button
            type="button"
            className="ss-btn ss-btn--accent ss-cta-dark__action"
            onClick={() => booking.open(slug)}
          >
            {c.nav.bookCall}
          </button>
        </div>
      </section>
    </main>
  );
}
